import { Alert, Box, Button, Chip, Grid, ListItemButton, Paper, Stack, Typography } from '@mui/material'
import type { PerfOverview, PerfTimePoint } from '@api/types/performance'
import { StatTile } from './StatTile'
import { LatencyChart } from './LatencyChart'
import { SqlBlock } from './SqlBlock'
import { errorRateSeverity, formatCount, formatMs, formatPercent, latencySeverity } from './format'

interface OverviewTabProps {
  overview?: PerfOverview
  series?: PerfTimePoint[]
  seriesError?: boolean
  hours: number
  loading: boolean
  /** The overview request failed. Shown instead of the "nothing recorded" note, which would lie. */
  error?: boolean
  onRetry: () => void
  onRetrySeries: () => void
  onSelectRoute: (route: string, method: string) => void
}

/**
 * The tiles, the chart, and the two "what should I look at" lists.
 *
 * The ordering of those lists is the opinion this tab holds. Endpoints are ranked by p95, because a
 * tail latency is what a person feels; queries are ranked by TOTAL time, because a two-millisecond
 * query run four hundred thousand times costs more than a one-second query run twice, and ranking
 * queries by their own latency hides exactly that.
 */
export function OverviewTab({
  overview,
  series,
  seriesError,
  hours,
  loading,
  error,
  onRetry,
  onRetrySeries,
  onSelectRoute,
}: OverviewTabProps) {
  const hasData = (overview?.totalRequests ?? 0) > 0

  return (
    <Stack spacing={2}>
      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Could not load the performance overview. The numbers below are not zero, they are missing.
        </Alert>
      )}

      {!loading && !error && !hasData && (
        <Alert severity="info">
          Nothing has been recorded in this window yet. Both APIs write their accumulated totals once a
          minute, so a freshly deployed build takes about that long to appear here.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={3} lg={12 / 7}>
          <StatTile
            label="Requests"
            value={formatCount(overview?.totalRequests)}
            detail={`${overview?.requestsPerMinute.toFixed(1) ?? '—'} / min`}
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={12 / 7}>
          <StatTile
            label="p50"
            value={formatMs(overview?.p50Ms)}
            detail="Half of requests are faster"
            severity={overview ? latencySeverity(overview.p50Ms) : undefined}
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={12 / 7}>
          <StatTile
            label="p95"
            value={formatMs(overview?.p95Ms)}
            detail="One in twenty is slower"
            severity={overview ? latencySeverity(overview.p95Ms) : undefined}
            help="Traffic-weighted across every endpoint. This is the number a person notices, not the average."
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={12 / 7}>
          <StatTile
            label="p99"
            value={formatMs(overview?.p99Ms)}
            detail="One in a hundred is slower"
            severity={overview ? latencySeverity(overview.p99Ms) : undefined}
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={12 / 7}>
          <StatTile
            label="Server errors"
            value={formatPercent(overview?.errorRatePercent, 2)}
            detail={`${formatPercent(overview?.clientErrorRatePercent, 2)} client errors`}
            severity={overview ? errorRateSeverity(overview.errorRatePercent) : undefined}
            help="5xx responses and requests that threw. Client errors are counted separately: a 404 is not an outage."
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={12 / 7}>
          <StatTile
            label="Time in database"
            value={formatPercent(overview?.dbTimeSharePercent)}
            detail={`${overview?.avgQueriesPerRequest.toFixed(1) ?? '—'} queries / request`}
            help="Share of total request time spent waiting on Postgres. A high share points at queries; a low one points at the application."
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={12 / 7}>
          <StatTile
            label="Probes"
            value={
              overview == null
                ? '—'
                : overview.failingProbes > 0
                  ? `${overview.failingProbes} failing`
                  : overview.degradedProbes > 0
                    ? `${overview.degradedProbes} slow`
                    : 'All healthy'
            }
            detail={`${overview?.activeEndpoints ?? 0} endpoints active`}
            severity={
              overview == null
                ? undefined
                : overview.failingProbes > 0
                  ? 'error'
                  : overview.degradedProbes > 0
                    ? 'warning'
                    : 'success'
            }
            loading={loading}
          />
        </Grid>
      </Grid>

      <LatencyChart
        title="Latency and volume"
        points={series}
        hours={hours}
        loading={loading}
        error={seriesError}
        onRetry={onRetrySeries}
        height={320}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: { xs: 1.5, sm: 2 }, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Slowest endpoints
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              By p95, among endpoints called at least five times. Tap one to chart it.
            </Typography>

            {(overview?.slowestEndpoints.length ?? 0) === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nothing to show yet.
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {overview?.slowestEndpoints.map((row) => (
                  // A real button so it is focusable and opens on Enter/Space, not a hover-only div.
                  <ListItemButton
                    key={`${row.service}-${row.method}-${row.route}`}
                    onClick={() => onSelectRoute(row.route, row.method)}
                    aria-label={`Chart ${row.method} ${row.route}`}
                    sx={{ gap: 1, p: 1, minHeight: 44, borderRadius: 1, flexWrap: 'wrap' }}
                  >
                    <Chip label={row.method} size="small" variant="outlined" sx={{ minWidth: 58 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: 12,
                        wordBreak: 'break-all',
                      }}
                    >
                      {row.route}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      color={`${latencySeverity(row.p95Ms)}.main`}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {formatMs(row.p95Ms)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      ×{formatCount(row.count)}
                    </Typography>
                  </ListItemButton>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: { xs: 1.5, sm: 2 }, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Heaviest queries
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              By total time consumed, not by how slow each call is.
            </Typography>

            {(overview?.heaviestQueries.length ?? 0) === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nothing to show yet.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {overview?.heaviestQueries.slice(0, 5).map((row) => (
                  <Box key={`${row.service}-${row.fingerprint}`}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      sx={{ mb: 0.5, flexWrap: 'wrap', columnGap: 1, rowGap: 0.5, minWidth: 0 }}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        {formatMs(row.totalMs)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatCount(row.count)} calls, {formatMs(row.avgMs)} each
                      </Typography>
                      {row.topRoute && (
                        <Chip
                          label={row.topRoute}
                          size="small"
                          variant="outlined"
                          sx={{ maxWidth: '100%', fontSize: 10 }}
                        />
                      )}
                    </Stack>
                    <SqlBlock sql={row.sql} maxLines={2} />
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  )
}
